import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { Clock, Ban } from "lucide-react";

const DAY_IDX = { Domenica: 0, Lunedì: 1, Martedì: 2, Mercoledì: 3, Giovedì: 4, Venerdì: 5, Sabato: 6 };
const pad = (n) => String(n).padStart(2, "0");

function parseTime(t) {
  const m = /(\d{1,2})[:.](\d{2})/.exec(t || "");
  return m ? { h: +m[1], min: +m[2] } : { h: 22, min: 0 };
}

function nextOccurrence(events, specials, closed, now) {
  const candidates = [];
  let closedHit = null;
  const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  for (const ev of events) {
    const idx = DAY_IDX[ev.day];
    if (idx === undefined) continue;
    const { h, min } = parseTime(ev.time);
    for (let add = 0; add < 15; add++) {
      const d = new Date(now); d.setDate(now.getDate() + add); d.setHours(h, min, 0, 0);
      if (d.getDay() !== idx || d <= now) continue;
      const c = closed.get(ymd(d));
      if (c) { if (!closedHit || d < closedHit.date) closedHit = { date: d, title: ev.title, reason: c.reason }; continue; }
      candidates.push({ date: d, title: ev.title, category: ev.category, to: ev.category === "spa" ? "/spa-naturista" : "/club-prive" }); break;
    }
  }
  for (const s of specials) {
    const d = new Date(s.date + "T22:00:00");
    if (d > now && !closed.has(s.date)) candidates.push({ date: d, title: s.title, category: s.category, to: "/calendario" });
  }
  candidates.sort((a, b) => a.date - b.date);
  const next = candidates[0] || null;
  const closedNext = closedHit && (!next || closedHit.date < next.date) ? closedHit : null;
  return { next, closedNext };
}

export default function Countdown() {
  const { events, specialDates, closures } = useSite();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const { next, closedNext } = nextOccurrence(events, specialDates, new Map(closures.map((c) => [c.date, c])), now);
  if (!next && !closedNext) return null;
  const banner = closedNext && (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
      data-testid="closure-banner" className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#941B30]/60 bg-[#7A1426]/25 text-xs text-[#F3E5AB] text-center flex-wrap justify-center">
      <Ban className="w-3.5 h-3.5 text-[#941B30]" />
      <span className="capitalize">{closedNext.date.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</span>
      <span className="text-[#A39E9B]">· chiusura straordinaria{closedNext.reason ? ` — ${closedNext.reason}` : ""}</span>
    </motion.div>
  );
  if (!next) return banner;
  const diff = Math.max(0, next.date - now);
  const days = Math.floor(diff / 86400000), hours = Math.floor(diff / 3600000) % 24, mins = Math.floor(diff / 60000) % 60, secs = Math.floor(diff / 1000) % 60;
  const accent = next.category === "spa" ? "#C59B6D" : "#D4AF37";
  const when = next.date.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });

  return (
    <>
    {banner}
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
      data-testid="countdown-box" className="glass border rounded-2xl px-5 sm:px-6 py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-8 w-full sm:w-auto max-w-md sm:max-w-none" style={{ borderColor: `${accent}55` }}>
      <div className="text-center sm:text-left">
        <p className="text-[0.6rem] tracking-[0.35em] uppercase flex items-center gap-2 justify-center sm:justify-start" style={{ color: accent }}>
          <Clock className="w-3 h-3" /> Prossima Serata
        </p>
        <Link to={next.to} data-testid="countdown-title" className="font-display text-xl sm:text-2xl text-[#FBF8F5] hover:text-[#F3E5AB] transition-colors">{next.title}</Link>
        <p className="text-xs text-[#A39E9B] capitalize">{when}</p>
      </div>
      <div className="flex gap-3" data-testid="countdown-timer">
        {[["Giorni", days], ["Ore", hours], ["Min", mins], ["Sec", secs]].map(([l, v]) => (
          <div key={l} className="text-center min-w-[3.2rem]">
            <div className="font-display text-2xl sm:text-3xl tabular-nums" style={{ color: accent }}>{pad(v)}</div>
            <div className="text-[0.55rem] uppercase tracking-widest text-[#6E6865]">{l}</div>
          </div>
        ))}
      </div>
    </motion.div>
    </>
  );
}
